import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

// Counts documents whose `created_at` falls within the last 24h. Used to drive
// the "Novos" badge on the Documentos sidebar entry (#25). No per-user read
// state — the heuristic is a rolling 24h window so the badge naturally fades
// as docs age out.
export function useNewDocumentsCount(): number {
  const [count, setCount] = useState(0)

  useEffect(() => {
    let cancelled = false

    async function load() {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      const { count: c, error } = await supabase
        .from('documents')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', since)
      if (cancelled) return
      if (error) {
        setCount(0)
        return
      }
      setCount(c ?? 0)
    }

    void load()
    // Refresh every 60s so the rolling window stays current without paying for
    // a realtime channel on this path — the badge's own comment already allowed
    // a minute of lag, so the per-INSERT reload the channel added was pure
    // extra load and was removed in the 2026-09-09 incident fix. Hidden tabs
    // skip the tick; returning to the tab refreshes immediately.
    const tick = setInterval(() => {
      if (!document.hidden) void load()
    }, 60_000)
    const onVisible = () => {
      if (!document.hidden) void load()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return count
}
